import * as PF from "pathfinding";
import Phaser from "phaser";
import { AssetKeys } from "../assets";
import { GAME_HEIGHT, GAME_WIDTH, PLAYER_TARGET_Y, TERRAIN, TerrainType } from "../constants";
import { SONG_SYNC_CONFIG, type SongCollectiblePlacement } from "../songSyncConfig";
import type { MapModule, ObstacleData, PickupData, TerrainZone } from "../types";
import { BiomeRhythmSystem } from "./BiomeRhythmSystem";
import { LandmarkPlacementSystem } from "./LandmarkPlacementSystem";
import { MapDecorationSystem } from "./MapDecorationSystem";

class Rng {
  constructor(private state = 424242) {}
  next() {
    this.state = (this.state * 1664525 + 1013904223) >>> 0;
    return this.state / 4294967296;
  }
  range(min: number, max: number) {
    return min + (max - min) * this.next();
  }
  int(min: number, max: number) {
    return Math.floor(this.range(min, max + 1));
  }
  pick<T>(items: T[]): T {
    return items[Math.floor(this.next() * items.length)];
  }
}

const OBSTACLE_KEYS = [
  AssetKeys.obstacle.treeDead,
  AssetKeys.obstacle.treeTwisted,
  AssetKeys.obstacle.rootWall,
  AssetKeys.obstacle.rootCurve,
  AssetKeys.obstacle.log,
  AssetKeys.obstacle.rockHuge,
  AssetKeys.obstacle.rockCluster,
  AssetKeys.obstacle.ruin
];

const DECOR_KEYS = [AssetKeys.obstacle.bush, AssetKeys.obstacle.stump, AssetKeys.obstacle.toxic, AssetKeys.obstacle.thorn];

interface ClearSpawnAreaOptions {
  protectVisible?: boolean;
}

export class MapSystem {
  modules: MapModule[] = [];
  private rng = new Rng();
  private id = 0;
  private spawnedEnergyHitTimes = new Set<number>();

  reset() {
    this.modules = [];
    this.rng = new Rng();
    this.id = 0;
    this.spawnedEnergyHitTimes.clear();
    let y = SONG_SYNC_CONFIG.mapEnergyHits.enabled ? Math.min(-1050, this.getOpeningEnergyTargetY() - 360) : -1050;
    while (y < GAME_HEIGHT + 420) {
      const module = this.createModule(y, this.estimateTime(y));
      this.modules.push(module);
      y += module.height;
    }
    if (SONG_SYNC_CONFIG.mapEnergyHits.enabled) this.ensureOpeningEnergy();
  }

  update(dt: number, worldSpeed: number, elapsed: number) {
    const dy = worldSpeed * dt;
    this.modules.forEach((module) => {
      module.y += dy;
      module.safePath.forEach((point) => (point.y += dy));
      module.terrain.forEach((zone) => (zone.y += dy));
      module.obstacles.forEach((obstacle) => (obstacle.y += dy));
      module.pickups.forEach((pickup) => (pickup.y += dy));
    });
    this.modules = this.modules.filter((module) => module.y < GAME_HEIGHT + 720);

    let top = Math.min(...this.modules.map((module) => module.y));
    while (top > -760) {
      const next = this.createModule(top - this.pickHeight(elapsed), elapsed + Math.abs(top) / 520);
      this.modules.unshift(next);
      top = next.y;
    }
  }

  getTerrainAt(x: number, y: number): TerrainType {
    for (let i = this.modules.length - 1; i >= 0; i -= 1) {
      for (let j = this.modules[i].terrain.length - 1; j >= 0; j -= 1) {
        const zone = this.modules[i].terrain[j];
        if (this.inEllipse(x, y, zone)) return zone.type;
      }
    }
    return "dirt";
  }

  getTerrainSpeed(x: number, y: number) {
    return TERRAIN[this.getTerrainAt(x, y)].speed;
  }

  getAllTerrain() {
    return this.modules.flatMap((module) => module.terrain);
  }

  getAllObstacles() {
    return this.modules.flatMap((module) => module.obstacles);
  }

  getAllPickups() {
    return this.modules.flatMap((module) => module.pickups).filter((pickup) => !pickup.collected);
  }

  getCurrentBiome(y: number = GAME_HEIGHT * 0.68) {
    const current = this.modules.find((module) => y >= module.y && y <= module.y + module.height);
    if (current?.biome) return current.biome;
    return this.modules.reduce((nearest, module) => {
      const currentDistance = Math.abs(module.y + module.height * 0.5 - y);
      const nearestDistance = Math.abs(nearest.y + nearest.height * 0.5 - y);
      return currentDistance < nearestDistance ? module : nearest;
    }, this.modules[0]).biome ?? "natural_path";
  }

  getPathXAt(y: number) {
    const module = this.findModuleAt(y);
    if (!module) return GAME_WIDTH * 0.5;
    return this.samplePath(module.safePath, y);
  }

  addPickupAt(pickup: PickupData) {
    const module = this.findModuleAt(pickup.y) ?? this.modules[0];
    if (!module) return;
    module.pickups.push(pickup);
  }

  clearSpawnArea(x: number, y: number, radius: number, options: ClearSpawnAreaOptions = {}) {
    this.modules.forEach((module) => {
      module.obstacles = module.obstacles.filter((obstacle) => {
        if (options.protectVisible && obstacle.y > -96 && obstacle.y < GAME_HEIGHT + 160) return true;
        const clearance = radius + Math.max(obstacle.width, obstacle.height) * 0.48;
        return Math.hypot(obstacle.x - x, obstacle.y - y) > clearance;
      });
    });
  }

  private findModuleAt(y: number) {
    return (
      this.modules.find((module) => y >= module.y && y <= module.y + module.height) ??
      this.modules.reduce((nearest, module) => {
        const currentDistance = Math.abs(module.y + module.height * 0.5 - y);
        const nearestDistance = Math.abs(nearest.y + nearest.height * 0.5 - y);
        return currentDistance < nearestDistance ? module : nearest;
      }, this.modules[0])
    );
  }

  private createModule(y: number, elapsed: number): MapModule {
    const height = this.pickHeight(elapsed);
    const id = `module-${this.id++}`;
    const safePath = this.createSafePath(y, height, elapsed);
    const biome = BiomeRhythmSystem.pickBiome(elapsed);
    const module: MapModule = {
      id,
      y,
      height,
      safePath,
      terrain: [],
      obstacles: [],
      pickups: [],
      biome,
      valid: false
    };
    this.addPathTerrain(module, elapsed);
    this.addSpecialTerrain(module, elapsed);
    this.addObstacles(module, elapsed);
    this.addPickups(module, elapsed);
    this.addEnergyHitPickups(module, elapsed);
    MapDecorationSystem.decorateModule(module, this.rng, elapsed);
    const profile = BiomeRhythmSystem.getProfile(biome);
    if (this.rng.next() < profile.landmarkChance) LandmarkPlacementSystem.decorateModule(module, this.rng);
    if (!this.validate(module)) this.repair(module);
    module.valid = this.validate(module);
    return module;
  }

  private pickHeight(elapsed: number) {
    if (elapsed > 96) return 470;
    if (elapsed > 50) return 560;
    return 640;
  }

  private createSafePath(y: number, height: number, elapsed: number) {
    const lanes = [108, 234, 360, 486, 612];
    const difficulty = Math.min(1, elapsed / 120);
    const start = this.rng.int(1, 3);
    const mid = Math.max(0, Math.min(4, start + this.rng.pick([-1, 0, 1, difficulty > 0.55 ? 2 : 0])));
    const end = Math.max(0, Math.min(4, mid + this.rng.pick([-1, 0, 1])));
    return [
      { x: lanes[start], y: y + 18 },
      { x: lanes[mid] + this.rng.range(-32, 32), y: y + height * 0.45 },
      { x: lanes[end] + this.rng.range(-24, 24), y: y + height - 18 }
    ];
  }

  private addPathTerrain(module: MapModule, elapsed: number) {
    const profile = BiomeRhythmSystem.getProfile(module.biome ?? "natural_path");
    for (let i = 0; i < 6; i += 1) {
      const t = i / 5;
      const y = module.y + module.height * t;
      const x = this.samplePath(module.safePath, y);
      const secondary = elapsed > 18 && i % 2 === 1;
      const type = secondary ? profile.pathSecondary : profile.pathPrimary;
      const width = profile.pathWidth * (secondary ? 0.82 : 1);
      const height = profile.pathHeight * (secondary ? 0.78 : 1);
      module.terrain.push(this.zone(module.id, type, x, y, width, height, profile.pathAlpha, "path"));
    }
  }

  private addSpecialTerrain(module: MapModule, elapsed: number) {
    const biome = module.biome ?? "natural_path";
    if ((biome === "corrupted_pressure_zone" || biome === "final_spirit_chase" || elapsed > 36) && this.rng.next() > 0.5) {
      const y = module.y + this.rng.range(module.height * 0.22, module.height * 0.78);
      const type = biome === "corrupted_pressure_zone" ? "swamp" : "mud";
      module.terrain.push(this.zone(module.id, type, this.rng.range(120, 600), y, this.rng.range(220, 360), this.rng.range(180, 280), 0.1, "slow"));
    }
    if ((biome === "rocky_open_area" || elapsed > 64) && this.rng.next() > 0.62) {
      const y = module.y + module.height * 0.5;
      module.terrain.push(this.zone(module.id, "water", GAME_WIDTH * 0.5, y, 720, 155, 0.22, "water", this.rng.range(-0.22, 0.22)));
      const x = this.samplePath(module.safePath, y);
      module.terrain.push(this.zone(module.id, "stone", x, y, 170, 118, 0.28, "path"));
    }
    if ((biome === "final_spirit_chase" || elapsed > 78) && this.rng.next() > 0.58) {
      module.terrain.push(this.zone(module.id, "fog", GAME_WIDTH * 0.5, module.y + module.height * 0.5, 660, module.height * 0.74, 0.08, "fog"));
    }
  }

  private addObstacles(module: MapModule, elapsed: number) {
    const profile = BiomeRhythmSystem.getProfile(module.biome ?? "natural_path");
    const count = Math.max(2, 4 + Math.floor(Math.min(1, elapsed / 120) * 4) + profile.obstacleBonus);
    for (let i = 0; i < count; i += 1) {
      const y = module.y + this.rng.range(46, module.height - 46);
      const safeX = this.samplePath(module.safePath, y);
      let x = this.rng.range(58, GAME_WIDTH - 58);
      if (Math.abs(x - safeX) < 118) {
        x = safeX + this.rng.pick([-1, 1]) * this.rng.range(130, 270);
      }
      x = Math.max(48, Math.min(GAME_WIDTH - 48, x));
      const decorative = this.rng.next() < 0.12;
      const key = decorative ? this.rng.pick(DECOR_KEYS) : this.rng.pick(this.getObstacleKeys(module.biome ?? "natural_path"));
      const base = decorative ? this.rng.range(62, 92) : this.rng.range(120, 190);
      module.obstacles.push({
        id: `${module.id}-obstacle-${i}`,
        key,
        x,
        y,
        width: base * (key.includes("log") || key.includes("rootWall") ? 1.32 : 1),
        height: base * (key.includes("tree") ? 1.22 : 0.9),
        rotation: this.rng.range(-0.42, 0.42),
        blocking: !decorative,
        decorative
      });
    }
  }

  private addPickups(module: MapModule, elapsed: number) {
    const profile = BiomeRhythmSystem.getProfile(module.biome ?? "natural_path");
    if (elapsed < 20 || this.rng.next() < 0.82 - profile.pickupChanceOffset * 0.4) return;
    const y = module.y + this.rng.range(module.height * 0.22, module.height * 0.82);
    const side = this.rng.pick([-1, 1]);
    const offset = side * this.rng.range(74, 138);
    module.pickups.push({
      id: `${module.id}-energy`,
      x: Math.max(46, Math.min(GAME_WIDTH - 46, this.samplePath(module.safePath, y) + offset)),
      y,
      radius: 18,
      collected: false,
      syncType: "soft_energy_orb",
      scoreValue: 1,
      energyRestoreMultiplier: 0.62,
      visualScale: 0.78,
      tint: 0xb7ff00
    });
  }

  private addEnergyHitPickups(module: MapModule, elapsed: number) {
    const config = SONG_SYNC_CONFIG.mapEnergyHits;
    if (!config.enabled) return;

    const moduleDuration = module.height / config.moduleTimeScalePx;
    const moduleStart = elapsed;
    const moduleEnd = elapsed + moduleDuration;

    config.hits.forEach((hit) => {
      if (this.spawnedEnergyHitTimes.has(hit.timeSec)) return;
      if (hit.timeSec <= SONG_SYNC_CONFIG.lyricAnchors.mirameFirstEnergySec + 0.1) return;
      if (hit.timeSec < moduleStart || hit.timeSec >= moduleEnd) return;

      const type = SONG_SYNC_CONFIG.collectibleTypes[hit.type];
      const progress = Phaser.Math.Clamp((hit.timeSec - moduleStart) / moduleDuration, 0.18, 0.82);
      const y = module.y + module.height * progress;
      const pathX = this.samplePath(module.safePath, y);
      const x = this.resolveEnergyHitX(pathX, hit.placement, hit.timeSec);

      module.pickups.push({
        id: `${module.id}-song-hit-${hit.timeSec.toFixed(2)}`,
        x,
        y,
        radius: 18 * type.visualScale,
        collected: false,
        syncType: hit.type,
        scoreValue: type.scoreValue,
        energyRestoreMultiplier: type.energyRestoreMultiplier,
        visualScale: type.visualScale,
        tint: type.tint,
        targetTimeSec: hit.timeSec,
        intensity: hit.strength
      });
      this.spawnedEnergyHitTimes.add(hit.timeSec);
    });
  }

  private ensureOpeningEnergy() {
    const targetY = this.getOpeningEnergyTargetY();
    const module = this.modules.find((item) => targetY >= item.y && targetY <= item.y + item.height);
    if (!module) return;
    const x = Phaser.Math.Clamp(this.samplePath(module.safePath, targetY), 54, GAME_WIDTH - 54);
    module.pickups.push({
      id: `${module.id}-opening-energy`,
      x,
      y: targetY,
      radius: 20,
      collected: false,
      syncType: "soft_energy_orb",
      scoreValue: 1,
      energyRestoreMultiplier: 1,
      visualScale: 0.96,
      tint: 0xb7ff00
    });
    this.spawnedEnergyHitTimes.add(SONG_SYNC_CONFIG.lyricAnchors.mirameFirstEnergySec);
  }

  private getOpeningEnergyTargetY() {
    return (
      PLAYER_TARGET_Y -
      SONG_SYNC_CONFIG.lyricAnchors.openingEnergyWorldSpeedPx * SONG_SYNC_CONFIG.lyricAnchors.mirameFirstEnergySec
    );
  }

  private resolveEnergyHitX(pathX: number, placement: SongCollectiblePlacement, timeSec: number) {
    if (placement === "left_safe") return Phaser.Math.Clamp(pathX - 86, 54, GAME_WIDTH - 54);
    if (placement === "right_safe") return Phaser.Math.Clamp(pathX + 86, 54, GAME_WIDTH - 54);
    if (placement === "slight_zigzag_safe") {
      const direction = Math.sin(timeSec * 2.7) >= 0 ? 1 : -1;
      return Phaser.Math.Clamp(pathX + direction * 52, 54, GAME_WIDTH - 54);
    }
    return Phaser.Math.Clamp(pathX, 54, GAME_WIDTH - 54);
  }

  private zone(moduleId: string, type: TerrainType, x: number, y: number, width: number, height: number, alpha: number, role?: TerrainZone["role"], rotation = this.rng.range(-0.45, 0.45)): TerrainZone {
    return { id: `${moduleId}-${type}-${Math.floor(x)}-${Math.floor(y)}`, type, x, y, width, height, alpha, rotation, role };
  }

  private validate(module: MapModule) {
    const cols = 20;
    const rows = 32;
    const grid = new PF.Grid(cols, rows);
    for (const obstacle of module.obstacles) {
      if (!obstacle.blocking) continue;
      const c = Math.floor((obstacle.x / GAME_WIDTH) * cols);
      const r = Math.floor(((obstacle.y - module.y) / module.height) * rows);
      for (let dx = -1; dx <= 1; dx += 1) {
        for (let dy = -1; dy <= 1; dy += 1) {
          if (grid.isInside(c + dx, r + dy)) grid.setWalkableAt(c + dx, r + dy, false);
        }
      }
    }
    const start = module.safePath[0];
    const end = module.safePath[module.safePath.length - 1];
    const sx = Math.max(0, Math.min(cols - 1, Math.floor((start.x / GAME_WIDTH) * cols)));
    const sy = 0;
    const ex = Math.max(0, Math.min(cols - 1, Math.floor((end.x / GAME_WIDTH) * cols)));
    const ey = rows - 1;
    const path = new PF.AStarFinder({ allowDiagonal: true, dontCrossCorners: true }).findPath(sx, sy, ex, ey, grid);
    return path.length > 0;
  }

  private repair(module: MapModule) {
    module.obstacles = module.obstacles.filter((obstacle) => {
      if (!obstacle.blocking) return true;
      const safeX = this.samplePath(module.safePath, obstacle.y);
      return Math.abs(obstacle.x - safeX) > 126;
    });
  }

  private estimateTime(y: number) {
    return Math.min(120, Math.abs(y) / 520);
  }

  private getObstacleKeys(biome: NonNullable<MapModule["biome"]>) {
    if (biome === "ancient_ruins") {
      return [AssetKeys.obstacle.ruin, AssetKeys.obstacle.rockHuge, AssetKeys.obstacle.rockCluster, AssetKeys.obstacle.log];
    }
    if (biome === "dense_forest_corridor" || biome === "dark_forest_entry") {
      return [AssetKeys.obstacle.treeDead, AssetKeys.obstacle.treeTwisted, AssetKeys.obstacle.rootWall, AssetKeys.obstacle.rootCurve, AssetKeys.obstacle.log];
    }
    if (biome === "corrupted_pressure_zone" || biome === "final_spirit_chase") {
      return [AssetKeys.obstacle.rootWall, AssetKeys.obstacle.rootCurve, AssetKeys.obstacle.treeDead, AssetKeys.obstacle.toxic, AssetKeys.obstacle.rockHuge];
    }
    if (biome === "rocky_open_area") {
      return [AssetKeys.obstacle.rockHuge, AssetKeys.obstacle.rockCluster, AssetKeys.obstacle.ruin, AssetKeys.obstacle.log];
    }
    return OBSTACLE_KEYS;
  }

  private samplePath(points: Array<{ x: number; y: number }>, y: number) {
    for (let i = 0; i < points.length - 1; i += 1) {
      const a = points[i];
      const b = points[i + 1];
      if (y >= a.y && y <= b.y) {
        const t = (y - a.y) / (b.y - a.y);
        return a.x + (b.x - a.x) * t;
      }
    }
    return points[points.length - 1].x;
  }

  private inEllipse(x: number, y: number, zone: TerrainZone) {
    const cos = Math.cos(-zone.rotation);
    const sin = Math.sin(-zone.rotation);
    const dx = x - zone.x;
    const dy = y - zone.y;
    const lx = dx * cos - dy * sin;
    const ly = dx * sin + dy * cos;
    return (lx / (zone.width * 0.5)) ** 2 + (ly / (zone.height * 0.5)) ** 2 <= 1;
  }
}
